import { useEffect, useMemo, useState } from "react";
import { getHolidaySeason } from "../../distflowAPI/salesAnalyticsApi";
import Answer from "./Answer";
import SplitView from "./SplitView";
import { seasonAnswer } from "./answers";
import { shortRange } from "./format";
import "./HolidaySeason.css";

const PAGE = 25;
const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 });
const money = (value) => value == null ? "—" : currency.format(Number(value));
const date = (value, year = true) => value ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`)) : "";
const range = (start, end) => `${date(start, false)} – ${date(end)}`;
const signedPct = (value) => {
  if (value == null) return null;
  const number = Number(value);
  return `${number > 0 ? "+" : number < 0 ? "−" : ""}${Math.abs(number).toFixed(1)}%`;
};
const LEAD_WEEKS = [2, 3, 4, 6, 8];
const STATUS_ORDER = ["missing", "slipped", "grew", "new", "steady"];
const VIEWS = [
  { key: "customers", label: "Customers", noun: "Customer", action: "Open details for" },
  { key: "products", label: "Products", noun: "Product", action: "Open on the Products tab:" },
  { key: "categories", label: "Categories", noun: "Category", action: "Focus the page on" },
];

function weekChange(week) {
  const now = Number(week.sales || 0);
  const before = Number(week.previous_sales || 0);
  const diff = now - before;
  const percent = before ? (diff / Math.abs(before)) * 100 : null;
  const toneName = Math.abs(diff) >= 1000 && (percent == null || Math.abs(percent) >= 5) ? (diff > 0 ? "gain" : "decline") : "flat";
  return { diff, toneName };
}

function WeekBars({ weeks, finished }) {
  const max = Math.max(1, ...weeks.flatMap((week) => [Number(week.sales || 0), Number(week.previous_sales || 0)]));
  return <div className="season-weeks-scroll"><div className="season-weeks" role="list" aria-label="Sales by week of the season"
    style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(96px, 1fr))` }}>
    {weeks.map((week) => {
      const upcoming = week.sales == null;
      const closed = !upcoming && Math.abs(Number(week.sales || 0)) < 100 && Math.abs(Number(week.previous_sales || 0)) < 100;
      const height = (value) => `${Math.max(0, Number(value || 0)) / max * 100}%`;
      const change = weekChange(week);
      const sign = change.diff > 0 ? "+" : change.diff < 0 ? "−" : "";
      return <div role="listitem" key={week.start_date} className={`season-week season-week--${week.kind}${upcoming ? " is-upcoming" : ""}`}
        title={`${week.label}\nThis season ${range(week.start_date, week.end_date)}: ${upcoming ? "not reached yet" : money(week.sales)}${!upcoming && !week.complete ? " (so far)" : ""}\nLast season ${range(week.previous_start_date, week.previous_end_date)}: ${money(week.previous_sales)}`}>
        <div className="season-week-bars" aria-hidden="true">
          {closed ? <span className="season-week-closed">Closed both years</span> : <>
            <span className="season-week-bar season-week-bar--current" style={{ height: upcoming ? "100%" : height(week.sales) }} />
            <span className="season-week-bar season-week-bar--previous" style={{ height: height(week.previous_sales) }} />
          </>}
        </div>
        <strong>{week.label}</strong>
        <span className="season-week-dates">{shortRange(week.start_date, week.end_date, Number(String(week.start_date).slice(0, 4)))}</span>
        {upcoming ? <span className="season-week-now">Coming up</span>
          : closed ? <span className="season-week-now">Closed</span>
            : <span className="season-week-now">{compact.format(Number(week.sales))}{!week.complete && !finished ? " so far" : ""}</span>}
        <small>last {compact.format(Number(week.previous_sales || 0))}</small>
        {!upcoming && !closed && <span className={`season-week-diff sa-delta--${change.toneName}`}>{sign}{compact.format(Math.abs(change.diff))}</span>}
      </div>;
    })}
  </div></div>;
}

export default function HolidaySeason({ territoryId, filters = [], onSelect, openKey, onOpenCustomer, onOpenProduct, renderCustomerPanel }) {
  const [season, setSeason] = useState("");
  const [year, setYear] = useState("");
  const [leadWeeks, setLeadWeeks] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("customers");
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const filterSignature = filters.join("|");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => getHolidaySeason({
        season: season || undefined, year: year || undefined, lead_weeks: leadWeeks || undefined,
        territory_id: territoryId, filters: filterSignature ? filterSignature.split("|") : [],
      }, { signal: controller.signal }))
      .then((result) => { if (!controller.signal.aborted) { setData(result || null); setStatus(null); setVisible(PAGE); } })
      .catch((requestError) => {
        if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
        setData(null);
        setError("The season comparison could not be loaded.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [season, year, leadWeeks, territoryId, filterSignature]);

  const group = data?.[view];
  const activeStatus = status ?? (group?.counts?.missing ? "missing" : "all");
  const rows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return (group?.rows || []).filter((row) =>
      (activeStatus === "all" || row.status === activeStatus) &&
      (!term || `${row.name} ${row.sku || ""}`.toLocaleLowerCase().includes(term)));
  }, [group, activeStatus, query]);

  if (!data && !loading && !error) return null;
  const currentView = VIEWS.find((item) => item.key === view) || VIEWS[0];
  const open = (row) => {
    if (view === "customers") onOpenCustomer?.(row.key);
    else if (view === "products") onOpenProduct?.(row.key);
    else onSelect?.(row);
  };
  const panel = view === "customers" && openKey && renderCustomerPanel ? renderCustomerPanel : null;
  const labels = data?.status_labels || {};
  const finished = data?.status === "finished";
  const notStarted = data?.status === "not_started";
  const selectedYear = data?.hebrew_year;
  const yearOptions = selectedYear ? [selectedYear + 1, selectedYear, selectedYear - 1, selectedYear - 2] : [];
  const missingLabel = finished ? "Didn’t order" : labels.missing;

  const answer = seasonAnswer(data);
  const statusCount = activeStatus === "all" ? group?.total : group?.counts?.[activeStatus];
  const lastSeasonHeader = finished ? "Last season" : "Last season at this point";
  const thisSeasonHeader = finished ? "This season" : "So far";
  const columns = activeStatus === "missing" ? ["last"] : activeStatus === "new" ? ["now"] : ["now", "last", "change"];

  return <>
  {answer ? <Answer answer={answer} /> : loading && <Answer loading />}
  <SplitView panel={panel}><section className="season" aria-label="Yom Tov season" aria-busy={loading}>
    {error && <p className="season-error" role="alert">{error}</p>}
    {data && <div className={`season-body${loading ? " season-body--refreshing" : ""}`}>
      <div className="season-controls">
        <span className="sa-word">Comparing</span>
        <label><span className="sa-visually-hidden">Season</span>
          <select value={data.season} onChange={(event) => { setSeason(event.target.value); setYear(""); }}>
            {Object.entries(data.seasons || {}).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label><span className="sa-visually-hidden">Year</span>
          <select value={selectedYear} onChange={(event) => { setSeason(data.season); setYear(Number(event.target.value)); }}>
            {yearOptions.map((option) => <option key={option} value={option}>{option} against {option - 1}</option>)}
          </select>
        </label>
        <span className="sa-word">starting</span>
        <label><span className="sa-visually-hidden">Weeks before Yom Tov</span>
          <select value={data.lead_weeks} onChange={(event) => { setSeason(data.season); setYear(selectedYear); setLeadWeeks(Number(event.target.value)); }}>
            {LEAD_WEEKS.map((option) => <option key={option} value={option}>{option} weeks before Yom Tov</option>)}
          </select>
        </label>
        <span className={`season-status season-status--${data.status}`}>
          {notStarted ? `Starts ${date(data.current.start_date)}` : finished ? `Season over ${date(data.current.end_date, false)}` : `Day ${data.days_in} of ${data.season_days}`}
        </span>
      </div>

      <section className="sa-box season-strip" aria-labelledby="season-weeks-title">
        <div className="sa-box-head">
          <div>
            <h3 className="sa-box-title" id="season-weeks-title">Week by week</h3>
            <p className="sa-box-sub">Lined up by Hebrew date, so each week has one Shabbos in both years. {data.previous.hebrew_year} ran {range(data.previous.start_date, data.previous.end_date)}.</p>
          </div>
          <div className="season-legend" aria-hidden="true"><span><i className="season-swatch--current" />{data.hebrew_year}, this season</span><span><i className="season-swatch--previous" />{data.previous.hebrew_year}, last season</span><span><i className="season-swatch--yomtov" />Yom Tov week</span></div>
        </div>
        <WeekBars weeks={data.weeks || []} finished={finished} />
      </section>

      <section className="sa-list season-lists" aria-labelledby="season-list-title">
        <div className="sa-list-head">
          <h3 id="season-list-title">Who changed this season</h3>
          <div className="sa-segmented" role="group" aria-label="Season breakdown">
            {VIEWS.map((item) => <button type="button" key={item.key} aria-pressed={view === item.key}
              className={view === item.key ? "is-selected" : ""} onClick={() => { setView(item.key); setStatus(null); setQuery(""); setVisible(PAGE); }}>{item.label}</button>)}
          </div>
        </div>
        <div className="sa-chips" role="group" aria-label="Filter by change">
          {STATUS_ORDER.filter((key) => group?.counts?.[key]).map((key) => <button type="button" key={key} aria-pressed={activeStatus === key}
            className={activeStatus === key ? "is-selected" : ""} onClick={() => { setStatus(key); setVisible(PAGE); }}>
            {key === "missing" ? missingLabel : labels[key]}<b>{group.counts[key]}</b></button>)}
          <button type="button" aria-pressed={activeStatus === "all"} className={activeStatus === "all" ? "is-selected" : ""} onClick={() => { setStatus("all"); setVisible(PAGE); }}>All<b>{group?.total || 0}</b></button>
        </div>
        {activeStatus === "missing" && rows.length > 0 && <p className="sa-note">{notStarted
          ? "Bought last season. Worth a call before the season starts."
          : finished ? "Bought last season, nothing this season." : "Bought by this point last season (or later in the season) but haven’t ordered yet."}</p>}
        <div className="sa-tools">
          <input type="search" aria-label={`Search ${view}`} placeholder={`Search ${view}`} value={query} onChange={(event) => { setQuery(event.target.value); setVisible(PAGE); }} />
        </div>
        {rows.length ? <div className="sa-table-scroll"><table className="sa-list-table season-table" aria-label={`Season ${view}`}>
          <thead><tr><th scope="col" className="sa-col-name">{currentView.noun}</th>
            {activeStatus === "all" && <th scope="col">Change</th>}
            {columns.includes("now") && <th scope="col" className="sa-num">{thisSeasonHeader}</th>}
            {columns.includes("last") && <th scope="col" className="sa-num">{activeStatus === "missing" ? "Spent last season" : lastSeasonHeader}</th>}
            {columns.includes("change") && <th scope="col" className="sa-num">Difference</th>}
            {activeStatus === "missing" && <th scope="col" className="sa-col-secondary"><span className="sa-visually-hidden">Size</span></th>}
          </tr></thead>
          <tbody>{rows.slice(0, visible).map((row) => {
            const change = Number(row.change_amount);
            const toneName = Math.abs(change) >= 1000 && (row.change_percent == null || Math.abs(Number(row.change_percent)) >= 15) ? (change > 0 ? "gain" : "decline") : "flat";
            return <tr key={row.key} className={openKey === row.key ? "is-open" : ""} onClick={() => open(row)}>
              <th scope="row" className="sa-col-name">
                <button type="button" className="sa-row-link" title={`${currentView.action} ${row.name}`} aria-current={openKey === row.key ? "true" : undefined}
                  onClick={(event) => { event.stopPropagation(); open(row); }}>{row.name}</button>
                {row.sku && <small>{row.sku}</small>}
              </th>
              {activeStatus === "all" && <td className="sa-cell-plain" data-label="Change"><span className={`sa-badge sa-badge--${row.status}`}>{row.status === "missing" ? missingLabel : labels[row.status]}</span></td>}
              {columns.includes("now") && <td className="sa-num" data-label={thisSeasonHeader}>{money(row.sales)}</td>}
              {columns.includes("last") && <td className="sa-num" data-label={activeStatus === "missing" ? "Spent last season" : lastSeasonHeader}>{money(activeStatus === "missing" ? row.last_season_total ?? row.previous_sales : row.previous_sales)}</td>}
              {columns.includes("change") && <td className={`sa-num sa-delta--${toneName}`} data-label="Difference">
                {change > 0 ? "+" : change < 0 ? "−" : ""}{money(Math.abs(change))}
                {row.change_percent != null && <small>{signedPct(row.change_percent)}</small>}</td>}
              {activeStatus === "missing" && <td className="sa-col-secondary season-size" aria-hidden="true">
                <span className="sa-meter"><b className="is-office" style={{ width: `${Math.max(2, (Number(row.last_season_total ?? row.previous_sales ?? 0) / Math.max(1, ...rows.map((item) => Number(item.last_season_total ?? item.previous_sales ?? 0)))) * 100)}%` }} /></span></td>}
            </tr>;
          })}</tbody>
        </table></div> : <p className="sa-list-empty">Nothing in this group.</p>}
        <div className="sa-list-foot">
          <span>{rows.length ? `Showing ${Math.min(visible, rows.length)} of ${statusCount ?? rows.length}.` : ""}
            {statusCount > rows.length && !query ? ` The biggest ${rows.length} by last season are listed.` : ""}</span>
          {rows.length > visible && <button type="button" className="sa-link-button" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more</button>}
        </div>
      </section>
    </div>}
  </section></SplitView>
  </>;
}
